import type { ActiveAssetRef } from "@elaso/shared/schemas/active-assets";
import { ulid } from "ulid";
import { ws } from "./websocket-wrapper";

// Client half of the live-lesson delivery tier
// (docs/adr/23-the-live-lesson-delivery-tier.md, TECH-3118). Tracks which assets
// are currently MOUNTED as a full view, a channel view or a thread panel but not
// a preview or peek, and declares the complete set to the server through
// `asset.setActive` whenever it changes. The server subscribes the connection to
// the matching `active:{assetKey}` topics, which carry viewer-only events,
// typing today, instead of the eager asset topic.
//
// Always the FULL set, never deltas, so it is idempotent and self-healing. A
// dropped update or a reconnect converges on the next declare. Window focus and
// tab visibility are deliberately ignored in v1, because typing is start-only on
// the wire, so a blur-unsubscribe would have nothing to replay on refocus.

const DECLARE_DEBOUNCE_MS = 150;

/**
 * Refcounted registry of mounted views per asset. Refcounts rather than a plain
 * set, because two panes can hold the same asset, a channel open while its
 * thread panel targets the same grid, and React strict-mode double-mounts must
 * not flap the declaration.
 */
const mounted = new Map<string, { ref: ActiveAssetRef; count: number }>();

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const keyOf = (ref: ActiveAssetRef): string => `${ref.type}:${ref.id}`;

const buildDeclare = () => ({
	id: ulid(),
	procedure: "asset.setActive",
	data: {
		assets: Array.from(mounted.values(), (entry) => entry.ref),
	},
});

const sendActiveSet = (): void => {
	ws.send(JSON.stringify(buildDeclare()));
};

/**
 * Send the current set immediately and resolve once the server has applied it.
 *
 * For callers that must ORDER a follow-up request after the subscription is
 * live, such as a viewer-scoped snapshot fetch, where anything published between
 * the snapshot and the subscription would be lost (see
 * `useVoiceStageActiveAsset`). Everything else should let `scheduleDeclare`
 * coalesce and stay fire-and-forget, because the declare is self-healing and
 * awaiting every navigation would put a pending call, and its timeout, on the
 * wire for no benefit.
 */
export const flushActiveAssets = (): Promise<void> => {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
	return ws.sendWithPromise<void>(buildDeclare());
};

// Coalesce a navigation's unmount and mount pair, and any same-frame churn,
// into one declare.
const scheduleDeclare = (): void => {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}
	debounceTimer = setTimeout(() => {
		debounceTimer = null;
		sendActiveSet();
	}, DECLARE_DEBOUNCE_MS);
};

export const acquireActiveAsset = (ref: ActiveAssetRef): void => {
	const key = keyOf(ref);
	const entry = mounted.get(key);
	if (entry) {
		entry.count += 1;
		return;
	}
	mounted.set(key, { ref, count: 1 });
	scheduleDeclare();
};

/**
 * Whether some mounted full view currently holds this asset. Read by the unread
 * bookkeeping (TECH-3283). A message recorded for a mounted view is read the
 * moment it lands, so the unread marker never flashes for a conversation the
 * user is looking at.
 */
export const isViewMounted = (ref: ActiveAssetRef): boolean =>
	mounted.has(keyOf(ref));

export const releaseActiveAsset = (ref: ActiveAssetRef): void => {
	const key = keyOf(ref);
	const entry = mounted.get(key);
	if (!entry) {
		return;
	}
	entry.count -= 1;
	if (entry.count <= 0) {
		mounted.delete(key);
		scheduleDeclare();
	}
};

/**
 * Re-declare the current set unconditionally, with no debounce. Called after the
 * workspace bootstrap chain lands, which also runs on every reconnect
 * (TECH-3051), because a fresh server connection starts with an empty active set
 * and a declare raced against bootstrap can have been dropped by the
 * subordination gate (docs/adr/23-the-live-lesson-delivery-tier.md). The declare
 * handler awaits the server-side entitlement reconcile, so calling right after
 * the workspace gate is safe.
 */
export const resyncActiveAssets = (): void => {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
	sendActiveSet();
};
